import apiClient from "./client";
import {
  deriveRegistrationStatus,
  mapRestaurantResponse,
  type BackendRestaurantResponse,
} from "./adapters";
import type {
  LoginRequest,
  LoginResponse,
  User,
  RegistrationStatus,
  VerifyEmailRequest,
} from "@/types";

interface BackendAuthenticatedUser {
  id: number;
  email: string;
  name: string;
  emailVerified: boolean;
}

function mapAuthenticatedUser(user: BackendAuthenticatedUser): User {
  return {
    email: user.email,
    name: user.name,
    emailVerified: user.emailVerified,
  };
}

export const authApi = {
  login: async (data: LoginRequest): Promise<LoginResponse> => {
    await apiClient.post<string>("/auth/login", data);
    return authApi.getMe();
  },

  logout: async (): Promise<void> => {
    await apiClient.get("/auth/logout");
  },

  getMe: async (): Promise<{ user: User; status: RegistrationStatus }> => {
    const response = await apiClient.get<BackendAuthenticatedUser>("/auth/me", {
      skipAuthRedirect: true,
    });

    const user = mapAuthenticatedUser(response.data);
    if (!user.emailVerified) {
      return { user, status: "needsEmailVerification" };
    }

    const restaurantResponse = await apiClient.get<BackendRestaurantResponse>(
      "/restaurant/me",
      {
        skipAuthRedirect: true,
      }
    );
    const restaurant = mapRestaurantResponse(restaurantResponse.data);

    return {
      user,
      status: deriveRegistrationStatus(user, restaurant),
    };
  },

  verifyEmail: async (data: VerifyEmailRequest): Promise<void> => {
    await apiClient.get("/auth/verify-email", {
      params: { token: data.code },
    });
  },

  resendVerification: async (): Promise<void> => {
    await apiClient.post("/auth/resend-verification");
  },

  sendRecoveryEmail: async (email: string): Promise<void> => {
    await apiClient.post("/auth/send-recovery-email", { email });
  },

  recoverPassword: async (
    token: string,
    password: string
  ): Promise<void> => {
    await apiClient.post("/auth/recover-password", { token, password });
  },
};
